import { ElMessageBox } from "element-plus";
import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest,
  downloadWithSavePicker,
} from "~/apis/requests/useApiCall";
import { Role, User, UserWithCcStatus } from "~/types";
import { TableDataWithPagination } from "~/types/generics/tableDataWithPagination";
import { Pagination } from "~/types/pagination";
import { exportToExcel } from "~/utils/dataTableHelper";
import { UserStatusType } from "~/types/enums/userStatusType";

export const useUsersStore = definePiniaStore("usersStore", {
  state: () => ({
    _tableData: [] as UserWithCcStatus[],
    _loading: true,
    _filterData: {},
    _pagination: {} as Pagination,
    _selectedUserForEdit: {} as User,
    _allRoles: [] as Array<Role>,

    /*   hierarchy */

    _allOrganizationalAdmins: [] as User[],
    _allHeads: [] as User[],
    _allTeamLeads: [] as User[],
    _allMembers: [] as User[],

    showUsersFiltersModal: false,
    showUsersEditModal: false,
    showUsersCreateModal: false,
  }),
  actions: {
    async getUsers(pageNo = 1, sortBy = "id", ascending = true) {
      this._loading = true;
      const sortDir = ascending ? "asc" : "desc";
      let url = `agents?page=${pageNo}&pagination=true&sort_column=${sortBy}&dir=${sortDir}`;
      for (const [key, value] of Object.entries(this._filterData)) {
        if (!value && value !== 0) continue;
        url += `&${key}=${value}`;
      }
      const { response, error } = await getRequest<
        TableDataWithPagination<UserWithCcStatus>
      >(url);
      if (!error) {
        const { data, ...pagination } = response.data;
        this.$state._tableData = data;
        this.$state._pagination = pagination;
        this._loading = false;
      }
    },

    async getAllRoles() {
      const { response, error } = await getRequest<Role[]>("roles");
      if (!error) {
        this.$state._allRoles = response.data;
      }
    },
    async emailExists(email: string) {
      const { response, error } = await getRequest(
        `agents/email-exists?email=${email}`,
      );
      if (!error) {
        return response.data;
      }
    },
    async userNameExists(userName: string, id?: number) {
      let url = `agents/user-name-exists?user_name=${userName}`;

      if (id) {
        url += `&id=${id}`;
      }

      const { response, error } = await getRequest(url);

      if (!error) {
        return response.data;
      }
    },

    async createUser(user: any) {
      const { response, error } = await postRequest<any>(
        "agents",
        user,
        true,

        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      if (error) {
        ElMessageBox({
          title: "User creation failed",
          message: error?.response?.data.message,
          type: "error",
        });
        return;
      }
      return response.data;
    },
    async updateUser(userId: number, user: any) {
      const { response, error } = await postRequest<any>(
        `agents/${userId}`,
        user,
        true,

        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      if (error) {
        ElMessageBox({
          title: "User update failed",
          message: error?.response?.data.message,
          type: "error",
        });
        return;
      }
      return response.data;
    },
    async updateUserStatus(user: User, status: UserStatusType) {
      const { response, error } = await putRequest<User>(
        `agents/${user.id}/user-status`,
        { user_status: status },
      );
      if (!error) {
        return response.data;
      }
      return error.response?.data;
    },
    async exportTable() {
      const sortDir = this._filterData.dir || "asc";
      const sortBy = this._filterData.sort_column || "id";
      let url = `/agents/export?sort_column=${sortBy}&dir=${sortDir}`;

      for (const [key, value] of Object.entries(this._filterData)) {
        if (!value && key !== "user_status") continue;
        url += `&${key}=${value}`;
      }

      const fileName = `agents_export_${new Date().toISOString()}.xlsx`;

      await downloadWithSavePicker(url, fileName);
    },

    // mutations
    toggleUsersFiltersModal() {
      this.showUsersFiltersModal = !this.showUsersFiltersModal;
    },
    toggleUsersEditModal() {
      this.showUsersEditModal = !this.showUsersEditModal;
    },
    toggleUsersCreateModal() {
      this.showUsersCreateModal = !this.showUsersCreateModal;
    },
    setCreateModalVisibility(val: boolean) {
      this.$state.showUsersCreateModal = val;
    },
    closeUsersEditModal() {
      this.showUsersEditModal = false;
    },
    closeUsersCreateModal() {
      this.showUsersCreateModal = false;
    },
    setEditUserData(user: User) {
      this.$state._selectedUserForEdit = user;
    },
    setFilterData(filterData: object) {
      this._filterData = filterData;
    },
  },
  getters: {
    tableData: (state) => state._tableData,
    pagination: (state) => state._pagination,
    selectedUserForEdit: (state) => state._selectedUserForEdit,
    allRoles: (state) => state._allRoles,
  },
});
